import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { HIPAATIZER_ORIGINS } from '@/lib/hipaatizer';
import { createHipaatizerCompletionHandler } from '@/lib/hipaatizerCompletion';
import { getLeadTracking } from '@/lib/leadTracking';
import { CONSULT_WORKFLOW_ID, hipaatizerAttributionValues, hipaatizerIframeSrc, hipaatizerOpenUrl } from '@/lib/hipaatizerAttribution';
import { createHipaatizerAttributionController } from '@/lib/hipaatizerAttributionController';

export { hipaatizerIframeSrc } from '@/lib/hipaatizerAttribution';

/**
 * Renders a published HIPAAtizer form in place of an in-app form.
 * Answers go straight to HIPAAtizer; nothing is stored on this site.
 *
 * Preferred: form.workflow_id → same iframe HIPAAtizer's own embed script
 * builds (app.hipaatizer.com/workflow/<id>), with its heightChange resize
 * protocol. Fallback: form.embed_url in a fixed-height iframe.
 */
export default function HiPaaTizerEmbed({ form, title, onComplete }) {
  const { pathname, search } = useLocation();
  const label = title || form?.label || 'Secure patient form';
  const workflowId = form?.workflow_id || '';
  // Freeze the iframe URL for its lifetime. Later attribution changes use the
  // restricted provider command, preserving entered answers and completion state.
  const frameSpec = useMemo(() => {
    const tracking = workflowId === CONSULT_WORKFLOW_ID ? getLeadTracking() : {};
    return {
      src: workflowId ? hipaatizerIframeSrc(workflowId, tracking) : form?.embed_url || '',
      initialValues: hipaatizerAttributionValues(workflowId, tracking),
      openUrl: hipaatizerOpenUrl(form, tracking),
    };
  }, [workflowId, form?.embed_url]);
  const src = frameSpec.src;
  const frameRef = useRef(null);
  const [height, setHeight] = useState(workflowId ? 720 : 760);
  const [openUrl, setOpenUrl] = useState(frameSpec.openUrl);
  const [attributionStatus, setAttributionStatus] = useState('idle');
  const attributionRef = useRef(null);
  const routeRef = useRef({ pathname, search });

  useLayoutEffect(() => {
    setOpenUrl(frameSpec.openUrl);
    if (workflowId !== CONSULT_WORKFLOW_ID) return undefined;
    const controller = createHipaatizerAttributionController({
      getFrameWindow: () => frameRef.current?.contentWindow,
      formOrigin: HIPAATIZER_ORIGINS[0],
      initialValues: frameSpec.initialValues,
      readValues: () => {
        const tracking = getLeadTracking();
        setOpenUrl(hipaatizerOpenUrl({ workflow_id: workflowId, embed_url: form?.embed_url }, tracking));
        return hipaatizerAttributionValues(workflowId, tracking);
      },
      onStatus: setAttributionStatus,
    });
    attributionRef.current = controller;
    const onMessage = event => controller.handleMessage(event);
    const onConsent = () => controller.refresh();
    // Install before a cached iframe can deliver its ready event.
    window.addEventListener('message', onMessage);
    window.addEventListener('tda-consent-change', onConsent);
    controller.refresh();
    return () => {
      controller.dispose();
      attributionRef.current = null;
      window.removeEventListener('message', onMessage);
      window.removeEventListener('tda-consent-change', onConsent);
    };
  }, [frameSpec, workflowId, form?.embed_url]);

  useLayoutEffect(() => {
    const previous = routeRef.current;
    routeRef.current = { pathname, search };
    if (previous.pathname !== pathname || previous.search !== search) {
      attributionRef.current?.refresh();
    }
  }, [pathname, search]);

  useEffect(() => {
    if (!src) return undefined;
    let formOrigin = '';
    try {
      const origin = new URL(src).origin;
      if (HIPAATIZER_ORIGINS.includes(origin)) formOrigin = origin;
    } catch { /* An invalid or unapproved embed cannot report completion. */ }
    const handleCompletion = createHipaatizerCompletionHandler({
      getFrameWindow: () => frameRef.current?.contentWindow,
      formOrigin,
      onComplete,
    });
    function onMessage(event) {
      if (!formOrigin || event.origin !== formOrigin || !frameRef.current || event.source !== frameRef.current.contentWindow) return;
      // The documented whole-workflow event is distinct from step-level SubmitSuccess.
      // Its data is deliberately not inspected or forwarded to the measurement callback.
      if (handleCompletion(event)) {
        frameRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      const data = event.data;
      if (!workflowId || !data || data.workflowId !== workflowId) return;
      if (data.type === 'heightChange' && Number.isFinite(data.height)) {
        setHeight(Math.max(240, Math.round(data.height) + 20));
      } else if (data.type === 'submitSuccess') {
        // Legacy UI message only: it is not evidence of whole-workflow completion.
        setTimeout(() => frameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [src, workflowId, onComplete]);

  if (!src) return null;

  return (
    <section className="rounded-2xl border border-amber-200 bg-white p-4 text-slate-900 shadow-xl sm:p-7" aria-label={label} data-hipaatizer-attribution={workflowId === CONSULT_WORKFLOW_ID ? attributionStatus : undefined}>
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-800">
        <ShieldCheck className="h-4 w-4" aria-hidden="true" />
        HIPAA-compliant secure form
      </p>
      <h2 className="mt-2 text-2xl font-light">{label}</h2>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">
        Your answers are submitted directly to our HIPAA-compliant forms provider and are not stored on this website.
      </p>
      <iframe
        ref={frameRef}
        src={src}
        title={label}
        className="mt-4 w-full rounded-lg border-0 bg-white"
        style={{ height: `${height}px`, overflow: 'hidden' }}
        scrolling="no"
        allow="camera; microphone; payment"
        loading="lazy"
      />
      <p className="mt-3 text-xs leading-relaxed text-slate-500">
        Form not loading?{' '}
        <a href={openUrl} target="_blank" rel="noopener noreferrer" className="text-amber-800 underline">
          Open it in a new tab
        </a>
        . Questions? Call (727) 228-6846.
      </p>
    </section>
  );
}
